// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import Button, { ButtonIcon } from "@insite/mobius/Button";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import { IconProps, IconWrapper } from "@insite/mobius/Icon";
import LazyImage, { LazyImageProps } from "@insite/mobius/LazyImage";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import { MappedLink } from "@insite/mobius/Menu";
import PanelRow, { PanelRowPresentationProps } from "@insite/mobius/PanelMenu/PanelRow";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import applyPropBuilder from "@insite/mobius/utilities/applyPropBuilder";
import getColor from "@insite/mobius/utilities/getColor";
import getProp from "@insite/mobius/utilities/getProp";
import InjectableCss, { StyledProp } from "@insite/mobius/utilities/InjectableCss";
import injectCss from "@insite/mobius/utilities/injectCss";
import MobiusStyledComponentProps from "@insite/mobius/utilities/MobiusStyledComponentProps";
import resolveColor from "@insite/mobius/utilities/resolveColor";
import uniqueId from "@insite/mobius/utilities/uniqueId";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import { buttonTransparent } from "@keller/XDL/PreStyle/button";
import { color, ellipsis } from "@keller/XDL/Utilities";
import React from "react";
import styled, { css, ThemeConsumer, ThemeProps, withTheme } from "styled-components";

export interface PanelMenuPresentationProps {
    /**
     * Indicates how the `css` property is combined with the variant `css` property from the theme.
     * If true, the variant css is applied first and then the component css is applied after causing
     * a merge, much like normal CSS. If false, only the component css is applied, overriding the variant css in the theme.
     */
    mergeCss?: boolean;
    /** CSS string or styled-components function to be injected into this component
     * @themable */
    cssOverrides?: {
        wrapper?: StyledProp<PanelMenuPresentationProps>;
        children?: StyledProp<PanelMenuPresentationProps>;
        menu?: StyledProp<PanelMenuPresentationProps>;
    };
    /** Props passed to the typography children of the menu.
     * @themable */
    childTypographyProps?: TypographyPresentationProps;
    /** Props passed to the menu close icon.
     * @themable */
    closeIconProps?: IconProps;
    /** Props passed to the menu back icon.
     * @themable */
    backIconProps?: IconProps;
    /** Color of the background of the menu header.
     * @themable */
    headerColor?: string;
    /** Color of the background of the menu body.
     * @themable */
    bodyColor?: string;
    /** Props to be passed into the panel row component.
     * @themable */
    panelRowProps?: PanelRowPresentationProps;
    /** XNG-Changes: For menu in level one */
    parentMenu?: string;
    /** XNG-Changes: For sub-menu in level two, three ...etc */
    menuTitle?: string;
    /** XNG-Changes: For clientAuthoredContent */
    clientAuthoredContent?: {
        title?: string;
        imageUrl?: string;
        link: { title?: string; url?: string };
    };
}

export type PanelMenuComponentProps = MobiusStyledComponentProps<
    "nav",
    {
        /** The element that triggers the menu. */
        panelTrigger: React.ReactElement<{ id: string; onClick: () => void; closeOverlay: () => void }>;
        /** Aria attributes used internally require an `id`. If not provided, a random id is generated. */
        descriptionId?: string;
        /**
         * The nested child structure that will be rendered by the menu.
         * If menu items are provided, children organically passed to the component
         * will not be rendered.
         */
        menuItems?: MappedLink[];
        /** Maximum number of menus that will appear in submenu panels. */
        maxDepth?: number;
        /** Function called to close overlay (Drawer) in which menu resides. Governs presence of `x` icons in headers. */
        closeOverlay?: () => void;
        /** The current URL of the application, used to apply 'current page' styles to the menu item */
        currentUrl?: string;
        /** @ignore internal prop */
        layer: number;
        /** Allow to open parent links */
        allowOpenParentLinks?: boolean;
        /**
         * Delays rendering children until the menu trigger is fired.
         * Useful for performance when rendering extremely large navigation trees.
         */
        delayRenderingChildren?: boolean;
        /** XNG-Changes: For menu in level one */
        parentMenu?: string;
        /** XNG-Changes: For sub-menu in level two, three ...etc */
        menuTitle?: string;
    }
>;

const PanelMenuWrapper = styled.nav<ThemeProps<BaseTheme> & InjectableCss>`
    display: flex;
    align-items: center;

    button,
    link {
        &:focus {
            outline-offset: -${getProp("theme.focus.width")};
        }
    }
    > button {
        width: 100%;
    }

    outline: none;
    ${injectCss}
`;

const PanelMenuStyle = styled.div<ThemeProps<BaseTheme> & InjectableCss & { color?: string }>`
    overflow-y: auto;
    height: calc(100% - 130px);
    background: ${({ color, theme }) => resolveColor(color, theme)};
    ${injectCss}
`;

const PanelChildren = styled.div<ThemeProps<BaseTheme> & InjectableCss & { displayLayer: number }>`
    max-height: 100vh;
    height: 100%;
    width: 100%;
    position: absolute;
    top: 0;
    left: 100%;
    transition: all ease-in-out ${getProp("theme.transition.duration.short")}ms;
    ${({ displayLayer }) => {
        if (displayLayer <= 0) {
            return css`
                left: 0;
            `;
        }
        return css`
            left: 100%;
        `;
    }}
    ${injectCss}
`;

const clientClientAuthoredContentStyles: {
    wrapper?: InjectableCss;
    title?: TypographyPresentationProps;
    image?: LazyImageProps;
    link?: LinkPresentationProps;
} = {
    wrapper: {
        css: css`
            padding: 20px 16px;
            display: flex;
            flex-direction: column;
        `,
    },
    title: {
        variant: "h4",
        color: "text.main",
        css: css`
            margin-bottom: 20px;
            ${ellipsis(2)}
        `,
    },
    image: {
        width: "293px",
        height: "227px",
        css: css`
            max-width: 100%;
            height: auto;
            margin: 0 auto 20px;
        `,
    },
    link: {
        typographyProps: {
            size: 14,
            ellipsis: true,
            weight: "bold",
            css: css`
                letter-spacing: 0;
                line-height: 16px;
            `,
        },
    },
};

export const RowWrapper = styled.div<ThemeProps<BaseTheme> & InjectableCss>`
    display: flex;
    align-items: center;
    justify-content: space-between;
    border-top: 1px solid ${getColor("common.border")};
    &.last-item {
        margin-bottom: 30px;
    }
    ${injectCss}
`;

/** PanelMenuProps with defaulted properties now listed as not-optional, plus theme. */
export type PanelMenuProps = PanelMenuComponentProps & PanelMenuPresentationProps;

type PanelMenuPropsCompleted = PanelMenuPresentationProps &
    Omit<PanelMenuComponentProps, "maxDepth"> &
    Required<Pick<PanelMenuComponentProps, "maxDepth">>;

class PanelMenu extends React.Component<PanelMenuPropsCompleted> {
    static defaultProps = { maxDepth: 3 };

    state = {
        layer: this.props.layer + 1,
        descriptionId: this.props.descriptionId || uniqueId(),
        delayRenderingChildren: this.props.delayRenderingChildren,
    };

    UNSAFE_componentWillReceiveProps(nextProps: PanelMenuPropsCompleted) {
        if (nextProps.layer !== this.props.layer + 1 && !nextProps.children && !this.props.children) {
            this.setState({ layer: nextProps.layer + 1 }, () => {});
        }
    }

    moveNextLeft = () => {
        this.setState(({ layer }: { layer: number }) => {
            return {
                delayRenderingChildren: false,
                layer: layer - 1,
            };
        });
    };

    moveNextRight = () => {
        this.setState(({ layer }: { layer: number }) => {
            return { layer: layer + 1 };
        });
    };

    renderMenuItems(childMenuItems: MappedLink[], currentDepth: number, propBuilder: any, closeOverlay?: () => void) {
        if (this.state.delayRenderingChildren) {
            return null;
        }
        const { applyProp } = propBuilder;
        const { currentUrl, allowOpenParentLinks } = this.props;
        const typographyProps = applyProp("childTypographyProps");
        const panelRowProps = applyProp("panelRowProps");
        // XNG-Changes
        const childMenuItemsFiltered = childMenuItems.filter(item => !item.excludeFromNavigation);

        return (
            <>
                {childMenuItemsFiltered.map((item, index) => {
                    const isLastItem = index === childMenuItemsFiltered.length - 1;
                    const renderChildren =
                        item.children?.filter(child => !child.excludeFromNavigation).length &&
                        currentDepth < this.props.maxDepth;
                    // XNG-Changes
                    // const isCurrent = !!currentUrl && (item.url === currentUrl || item.title === currentUrl);
                    // const clickableProps: { href?: string } = { ...item.clickableProps };
                    const isCurrent = !!currentUrl && item.url === currentUrl;
                    const forwardProps: { href?: string } = {};
                    // XNG-Changes - Remove !renderChildren
                    if (allowOpenParentLinks && item.url && !isCurrent) {
                        forwardProps.href = item.url;
                    }

                    let menu;
                    let menuItem: JSX.Element | null = (
                        <PanelRow
                            key={item.title}
                            isCurrent={isCurrent}
                            onClick={this.props.closeOverlay}
                            color={applyProp("bodyColor")}
                            // XNG-Changes
                            // hasChildren={renderChildren || false}
                            allowOpenParentLinks={allowOpenParentLinks}
                            tabIndex={this.state.layer === 0 ? 0 : -1}
                            {...forwardProps}
                            {...panelRowProps}
                        >
                            <Typography
                                data-test-selector={`${this.props["data-test-selector"]}_${item.title}`}
                                size={14}
                                color="text.link"
                                weight="bold"
                                {...typographyProps}
                            >
                                {item.title}
                            </Typography>
                        </PanelRow>
                    );

                    if (renderChildren) {
                        let panelRow;
                        if (item.url) {
                            panelRow = <PanelRow hasChildren></PanelRow>;
                        } else {
                            panelRow = React.cloneElement(menuItem, { hasChildren: true });
                            menuItem = null;
                        }
                        menu = (
                            <PanelMenu
                                {...this.props}
                                as="div"
                                // XNG-Changes
                                // key={item.title}
                                // panelTrigger={menuItem}
                                panelTrigger={panelRow}
                                delayRenderingChildren={true}
                                menuItems={item.children as MappedLink[]}
                                maxDepth={this.props.maxDepth - 1}
                                layer={this.state.layer}
                                // XNG-Changes
                                parentMenu={item.title}
                                menuTitle={item.title}
                            />
                        );
                    }
                    return (
                        <RowWrapper key={item.title} className={isLastItem ? "last-item" : ""}>
                            {menuItem}
                            {menu}
                        </RowWrapper>
                    );
                })}
            </>
        );
    }

    renderChildren = (layer: number, menuItems: MappedLink[] | undefined, propBuilder: any) => {
        // if menu closed, don't render the children
        if (layer > 0) {
            return null;
        }

        if (menuItems) {
            return this.renderMenuItems(menuItems, 1, propBuilder);
        }
        return this.props.children;
    };

    renderClientAuthoredContent = () => {
        const { clientAuthoredContent } = this.props;
        return (
            <StyledWrapper {...clientClientAuthoredContentStyles.wrapper}>
                <Typography {...clientClientAuthoredContentStyles.title}>{clientAuthoredContent?.title}</Typography>
                <LazyImage src={clientAuthoredContent?.imageUrl} {...clientClientAuthoredContentStyles.image} />
                {clientAuthoredContent?.link.url && (
                    <StyledWrapper>
                        <Link href={clientAuthoredContent?.link.url} {...clientClientAuthoredContentStyles.link}>
                            {clientAuthoredContent?.link.title || clientAuthoredContent?.link.url}
                        </Link>
                    </StyledWrapper>
                )}
            </StyledWrapper>
        );
    };

    render() {
        return (
            <ThemeConsumer>
                {theme => {
                    const {
                        currentUrl,
                        descriptionId,
                        menuItems,
                        panelTrigger,
                        closeOverlay,
                        mergeCss,
                        clientAuthoredContent,
                        ...otherProps
                    } = this.props;
                    const propBuilder = applyPropBuilder({ ...this.props, theme }, { component: "panelMenu" });
                    const { translate } = theme;
                    const { layer, descriptionId: navId } = this.state;
                    const { spreadProps, applyProp, applyStyledProp } = propBuilder;
                    const resolvedMergeCss = mergeCss ?? otherProps?.theme?.panelMenu?.defaultProps?.mergeCss;
                    const headerColor = applyProp("headerColor", "common.backgroundContrast");
                    return (
                        <PanelMenuWrapper
                            css={applyStyledProp(["cssOverrides", "wrapper"], resolvedMergeCss)}
                            {...{ "aria-labelledby": navId }}
                            {...otherProps}
                            tabIndex={-1}
                        >
                            {React.cloneElement(panelTrigger, { id: navId, onClick: this.moveNextLeft, closeOverlay })}
                            <PanelChildren
                                displayLayer={layer}
                                aria-hidden={layer > 0}
                                css={applyStyledProp(["cssOverrides", "children"], resolvedMergeCss)}
                                data-layer={layer}
                            >
                                <PanelMenuStyle
                                    css={applyStyledProp(["cssOverrides", "menu"], resolvedMergeCss)}
                                    color={applyProp("bodyColor", "common.white")}
                                >
                                    <PanelRow as="div" color="common.white" header>
                                        <Button
                                            color={headerColor}
                                            onClick={this.moveNextRight}
                                            tabIndex={layer === 0 ? 0 : -1}
                                            css={css`
                                                min-width: 0;
                                                display: flex;
                                                align-items: center;
                                                padding: 0;
                                                && {
                                                    height: 24px;
                                                }

                                                ${TypographyStyle}, ${IconWrapper} {
                                                    color: ${color("common.ui7")};
                                                }
                                                ${IconWrapper} {
                                                    margin-right: 16px;
                                                }
                                                ${buttonTransparent}
                                            `}
                                        >
                                            <ButtonIcon {...spreadProps("backIconProps")} />
                                            <Typography
                                                data-test-selector={`${this.props["data-test-selector"]}_${this.props.parentMenu}`}
                                                weight="bold"
                                                size={14}
                                                transform="uppercase"
                                            >
                                                {translate("Back")}
                                            </Typography>
                                            <VisuallyHidden>{translate("Back")}</VisuallyHidden>
                                        </Button>
                                        {/* {closeOverlay && ( */}
                                        {/*    <Button */}
                                        {/*        color={headerColor} */}
                                        {/*        onClick={closeOverlay} */}
                                        {/*        tabIndex={layer === 0 ? 0 : -1} */}
                                        {/*    > */}
                                        {/*        <ButtonIcon {...spreadProps("closeIconProps")} /> */}
                                        {/*        <VisuallyHidden>{translate("Close")}</VisuallyHidden> */}
                                        {/*    </Button> */}
                                        {/* )} */}
                                    </PanelRow>
                                    <PanelRow as="div" color="primary" header>
                                        <Typography size={14} weight="bold" transform="uppercase">
                                            {translate(this.props.parentMenu || "")}
                                        </Typography>
                                    </PanelRow>
                                    {clientAuthoredContent
                                        ? this.renderClientAuthoredContent()
                                        : this.renderChildren(layer, menuItems, propBuilder)}
                                </PanelMenuStyle>
                            </PanelChildren>
                        </PanelMenuWrapper>
                    );
                }}
            </ThemeConsumer>
        );
    }
}

/**
 * Menu composed of slide-in panels.
 */

PanelMenu.defaultProps = {
    maxDepth: 3,
};

/** @component */
export default withTheme((props: PanelMenuProps) => <PanelMenu {...(props as PanelMenuPropsCompleted)} />);

export * from "./PanelRow";
